import { stringToColour } from 'utils/chartUtils';

import { RiskRatings } from './RiskRatingConstants';

export const BASE_PADDING = 12;
export const PADDING_LG = 16;
export const BASE_MARGIN = 12;
export const MARGIN_XS = 8;
export const MARGIN_LG = 16;
export const PADDING_XL = 32;
export const MARGIN_XL = 32;
export const BASE_GUTTER: [number, number] = [BASE_PADDING, BASE_PADDING];
export const GUTTER_LARGE: [number, number] = [PADDING_LG, PADDING_LG];
export const GUTTER_XL: [number, number] = [PADDING_XL, PADDING_XL];
export const MIN_SCREEN_WIDTH = 1200;
export const FULL_MENU_WIDTH = 168;
export const HEADER_HEIGHT = 64;

const successColor = '#50C988';
const failedColor = '#E05252';
const inprogressColor = '#2192f9';
const backlogColor = '#FEB96A';

export const SEVERITY_COLOR: Record<string, string> = new Proxy<
  Record<string, string>
>(
  {
    critical: failedColor,
    high: '#FC8F66',
    medium: '#FEB96A',
    low: '#FAE5BB',
    info: '#8B8B8B',
  },
  {
    get: (target, prop: string | symbol) => {
      if (typeof prop !== 'string') return Reflect.get(target, prop);
      return target[prop] ?? target[prop.toLowerCase()];
    },
  },
);

export const WIDE_INTERNET_ACCESS_COLOR: Record<string, string> = new Proxy<
  Record<string, string>
>(
  {
    yes: '#ffb7b2',
    no: '#b6cfb6',
    partial: '#ecd88f',
    'N/A': '#ecd88f',
  },
  {
    get: (target, prop: string | symbol) => {
      if (typeof prop !== 'string') return Reflect.get(target, prop);
      return target[prop] ?? target[prop.toLowerCase()] ?? stringToColour(prop);
    },
  },
);

export const SEVERITY_COLOR_TEXT: Record<string, string> = new Proxy<
  Record<string, string>
>(
  {
    critical: '#D43030',
    high: '#E14E17',
    medium: '#CE7C1D',
    low: '#B48A38',
    info: '#8b8b8b',
  },
  {
    get: (target, prop: string | symbol) => {
      if (typeof prop !== 'string') return Reflect.get(target, prop);
      return target[prop] ?? target[prop.toLowerCase()];
    },
  },
);

export const PROGRESS_COLOR = {
  width: '33px',
  error: '#D43F3A',
  '0': '#108ee9',
  '100': '#87d068',
} as const;

export const COLORS: Record<string, string> = {
  success: successColor,
  passed: successColor,
  failed: failedColor,
  inprogress: inprogressColor,
  backlog: backlogColor,
  default: '#BFBFBF',
};

export const DONUT_CHART_COLORS: Record<string, string> = {
  others: '#d5d8dc',
};

export const AI_INVENTORY_TIME_SERIES_COLORS = {
  default: '#7839ED',
  riskLevel: {
    critical: failedColor,
    high: '#FC8F66',
    moderate: backlogColor,
    low: successColor,
    minimal_risk: successColor,
  },
  status: {
    approved: successColor,
    conditional: inprogressColor,
    pending: backlogColor,
    rejected: failedColor,
    reassessment: '#9B59B6',
    unreviewed: '#8B8B8B',
  },
} as const;

export const REPO_STATUS_COLOR: Record<string, string> = {
  ACTIVE: '#b7edcb',
  INACTIVE: '#f7b8c3',
  DORMANT: '#acd0f2',
};

export const FORM_LAYOUT = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
} as const;

export const MODAL_SIZE_ULTRA = 1150;
export const MODAL_SIZE_EXTRA_LARGE = 900;
export const MODAL_SIZE_LARGE = 800;
export const MODAL_SIZE_MEDIUM = 700;
export const MODAL_SIZE_SMALL = 400;
export const MODAL_SIZE_EXTRA_SMALL = 300;

export const DRAWER_SIZE_DEFAULT = 600;
export const DRAWER_SIZE_XXL = 1200;

export function hexToRgb(hex: string, a: number): string | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)},${a})`
    : null;
}

export const RiskRatingColors: Record<string, string> = {
  [RiskRatings.goodRiskScore]: '#54CA8B',
  [RiskRatings.fairRiskScore]: '#FEB96A',
  [RiskRatings.poorRiskScore]: '#FC8F66',
  [RiskRatings.veryPoorRiskScore]: '#E05252',
};

export const FINDING_SEVERITY_BREAKDOWN_WIDTH = 350;

export const ASSET_STATUS_COLOR: Record<string, string> = {
  ACTIVE: successColor,
  INACTIVE: failedColor,
};
